import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { supabase } from '../../lib/supabase';
import { debounce } from '../../lib/debounce';
import {
  e164WhatsappRegistro,
  REGISTER_WHATSAPP_COUNTRIES,
  validarWhatsappRegistro,
  type RegisterWhatsappIso,
} from '../../lib/phoneUtils';
import {
  validarApellidoRegistro,
  validarEmailRegistro,
  validarNombreRegistro,
  validarPasswordRegistro,
} from '../../lib/signupRules';
import {
  insertReferralAfterSignup,
  savePendingReferral,
  type ReferralLookupRow,
} from '../../lib/pendingReferralAttribution';

interface RegisterFormData {
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  password: string;
  confirmPassword: string;
  aceptoTerminos: boolean;
}

export const Register: React.FC = () => {
  const {
    register: registerUser,
    isLoading,
    error,
    clearError,
    registrationEmailPendingVerification,
    dismissRegistrationPending,
    resendSignupEmail,
  } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromLink = useMemo(
    () => (searchParams.get('ref') || '').toUpperCase().trim(),
    [searchParams]
  );
  const [showRefField, setShowRefField] = useState(() => Boolean(refFromLink));
  const [refCode, setRefCode] = useState(() => refFromLink);
  const [refCodeValid, setRefCodeValid] = useState<boolean | null>(null);
  const [refCodeData, setRefCodeData] = useState<ReferralLookupRow | null>(null);

  const [phoneIso, setPhoneIso] = useState<RegisterWhatsappIso>('CO');
  const phoneCountry =
    REGISTER_WHATSAPP_COUNTRIES.find((c) => c.iso === phoneIso) ?? REGISTER_WHATSAPP_COUNTRIES[0]!;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid }
  } = useForm<RegisterFormData>({
    mode: 'onChange'
  });

  const password = watch('password');
  const telefono = watch('telefono') || '';

  const validateRefCode = useCallback(async (code: string) => {
    if (!code || code.length < 2) {
      setRefCodeValid(null);
      setRefCodeData(null);
      return;
    }
    try {
      const { data, error } = await supabase.rpc('referral_code_lookup', {
        p_codigo: code,
      });
      if (error || !data?.length) {
        setRefCodeValid(false);
        setRefCodeData(null);
        return;
      }
      setRefCodeValid(true);
      setRefCodeData(data[0] as ReferralLookupRow);
    } catch {
      setRefCodeValid(false);
      setRefCodeData(null);
    }
  }, []);

  const debouncedValidateRef = useMemo(
    () =>
      debounce((code: string) => {
        void validateRefCode(code);
      }, 500),
    [validateRefCode]
  );

  useEffect(() => {
    if (refFromLink) {
      void validateRefCode(refFromLink);
    }
  }, [refFromLink, validateRefCode]);

  const onSubmit = async (data: RegisterFormData) => {
    // SECURITY: Never log passwords or sensitive form data
    console.log('Register form submitted');
    clearError();

    const applyReferralIfNeeded = async (emailForLookup: string) => {
      if (!refCodeValid || !refCodeData) return;
      const sameAsUrlRef = refFromLink.length > 0 && refCode === refFromLink;
      const canal: 'link' | 'code' = sameAsUrlRef ? 'link' : 'code';
      const emailNorm = emailForLookup.trim().toLowerCase();
      const { data: urow } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', emailNorm)
        .maybeSingle();
      if (!urow?.id) {
        console.warn('Referral: no usuarios row for attribution');
        return;
      }
      const empresaId = useAuthStore.getState().currentEmpresa?.id ?? null;
      await insertReferralAfterSignup(supabase, {
        referredUsuarioId: urow.id,
        referredEmpresaId: empresaId,
        row: refCodeData,
        canal,
      });
    };

    try {
      console.log('Starting user registration...');
      const rawPhone = data.telefono?.replace(/\D/g, '') || '';
      await registerUser(
        {
          nombre: `${data.nombre} ${data.apellido}`,
          email: data.email,
          telefono: rawPhone ? e164WhatsappRegistro(phoneIso, rawPhone) : undefined,
        },
        data.password
      );

      // Stamp T&C acceptance version (includes Vitrina Virtual enrollment clause).
      try {
        const { TERMINOS_VERSION_ACTUAL } = await import('../../constants/terminos');
        const emailNorm = data.email.trim().toLowerCase();
        await supabase
          .from('usuarios')
          .update({
            terminos_version: TERMINOS_VERSION_ACTUAL,
            terminos_aceptados_at: new Date().toISOString(),
          })
          .eq('email', emailNorm);
      } catch (tcErr) {
        console.warn('No se pudo registrar la aceptación de términos:', tcErr);
      }

      const after = useAuthStore.getState();
      if (after.registrationEmailPendingVerification) {
        if (refCodeValid && refCodeData) {
          const sameAsUrlRef = refFromLink.length > 0 && refCode === refFromLink;
          const canal: 'link' | 'code' = sameAsUrlRef ? 'link' : 'code';
          savePendingReferral({
            referral_code_id: refCodeData.id,
            tier: refCodeData.tier,
            canal,
          });
        }
        return;
      }
      if (after.error) {
        return;
      }

      if (refCodeValid && refCodeData) {
        try {
          await applyReferralIfNeeded(data.email);
        } catch (e) {
          console.error('Referral attribution failed:', e);
        }
      }

      const welcomeMsg =
        refCodeValid && refCodeData
          ? `¡Bienvenido! Registrado con el código de ${refCodeData.referrer_nombre}`
          : '¡Bienvenido a Clientelo!';
      try {
        sessionStorage.setItem('clientelo_referral_welcome', welcomeMsg);
      } catch {
        /* ignore */
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  if (registrationEmailPendingVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-green-100 text-2xl">
            ✉️
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900">Revisa tu correo</h2>
          <div className="rounded-lg bg-green-50 border border-green-200 p-5 text-left text-sm text-gray-700 space-y-3">
            <p>
              Te enviamos un enlace de verificación a{' '}
              <span className="font-semibold text-gray-900">{registrationEmailPendingVerification}</span>.
            </p>
            <p>
              Haz clic en el enlace del correo (revisa también spam) para activar tu cuenta. Después podrás{' '}
              <strong>iniciar sesión</strong> con tu correo y contraseña.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link
              to="/login"
              className="inline-flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Ir a iniciar sesión
            </Link>
            <button
              type="button"
              onClick={() => dismissRegistrationPending()}
              className="inline-flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              Registrar otro correo
            </button>
            <button
              type="button"
              disabled={resendBusy || !registrationEmailPendingVerification}
              onClick={async () => {
                if (!registrationEmailPendingVerification) return;
                setResendMessage(null);
                setResendBusy(true);
                try {
                  await resendSignupEmail(registrationEmailPendingVerification);
                  setResendMessage('Listo. Revisa tu bandeja y spam en unos minutos.');
                } catch (e) {
                  setResendMessage(e instanceof Error ? e.message : 'No se pudo reenviar.');
                } finally {
                  setResendBusy(false);
                }
              }}
              className="inline-flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 disabled:opacity-50"
            >
              {resendBusy ? 'Reenviando…' : 'Reenviar correo de verificación'}
            </button>
          </div>
          {resendMessage && (
            <p className="text-sm text-gray-600 max-w-md mx-auto">{resendMessage}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Crear Cuenta en Clientelo
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Únete a miles de empresas que ya usan nuestro CRM
          </p>
        </div>

        {/* Formulario de Registro */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Nombre y Apellido */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="nombre" className="form-label">
                  Nombre *
                </label>
                <input
                  id="nombre"
                  type="text"
                  autoComplete="given-name"
                  {...register('nombre', {
                    validate: validarNombreRegistro,
                  })}
                  className={`input-field ${errors.nombre ? 'border-red-500' : ''}`}
                  placeholder="Tu nombre"
                />
                {errors.nombre && (
                  <p className="mt-1 text-sm text-red-600">{errors.nombre.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="apellido" className="form-label">
                  Apellido *
                </label>
                <input
                  id="apellido"
                  type="text"
                  autoComplete="family-name"
                  {...register('apellido', {
                    validate: validarApellidoRegistro,
                  })}
                  className={`input-field ${errors.apellido ? 'border-red-500' : ''}`}
                  placeholder="Tu apellido"
                />
                {errors.apellido && (
                  <p className="mt-1 text-sm text-red-600">{errors.apellido.message}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Correo Electrónico *
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                  {...register('email', {
                    validate: validarEmailRegistro,
                  })}
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                placeholder="tu@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* WhatsApp / Teléfono */}
            <div>
              <label htmlFor="telefono" className="form-label">
                WhatsApp *
              </label>
              <div className={`flex items-center border rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`}>
                <label htmlFor="telefonoPais" className="sr-only">
                  País del WhatsApp
                </label>
                <select
                  id="telefonoPais"
                  value={phoneIso}
                  onChange={(e) => {
                    const next = e.target.value as RegisterWhatsappIso;
                    setPhoneIso(next);
                    const max = REGISTER_WHATSAPP_COUNTRIES.find((c) => c.iso === next)?.localMax ?? 10;
                    const digits = (telefono || '').replace(/\D/g, '').slice(0, max);
                    setValue('telefono', digits, { shouldValidate: true, shouldDirty: true });
                    void trigger('telefono');
                  }}
                  className="pl-2 pr-1 py-2 text-sm bg-transparent border-r border-gray-200 outline-none"
                >
                  {REGISTER_WHATSAPP_COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>
                      {c.flag} +{c.dial}
                    </option>
                  ))}
                </select>
                <input
                  id="telefono"
                  type="tel"
                  autoComplete="tel"
                  {...register('telefono', {
                    required: 'El número de WhatsApp es requerido',
                    validate: (value) => validarWhatsappRegistro(phoneIso, value || ''),
                  })}
                  value={telefono}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, phoneCountry.localMax);
                    setValue('telefono', digits, { shouldValidate: true, shouldDirty: true });
                  }}
                  className="flex-1 bg-transparent px-2 py-2 text-sm outline-none"
                  placeholder={phoneCountry.placeholder}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{phoneCountry.label}: {phoneCountry.hint}</p>
              {errors.telefono && (
                <p className="mt-1 text-sm text-red-600">{errors.telefono.message}</p>
              )}
            </div>

            {/* Contraseña */}
            <div>
              <label htmlFor="password" className="form-label">
                Contraseña *
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  {...register('password', {
                    validate: validarPasswordRegistro,
                  })}
                  className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <label htmlFor="confirmPassword" className="form-label">
                Confirmar Contraseña *
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  {...register('confirmPassword', {
                    required: 'La confirmación de contraseña es requerida',
                    validate: value => value === password || 'Las contraseñas no coinciden'
                  })}
                  className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Términos y Condiciones */}
            <div className="flex items-center">
              <input
                id="aceptoTerminos"
                type="checkbox"
                {...register('aceptoTerminos', {
                  required: 'Debes aceptar los términos y condiciones'
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="aceptoTerminos" className="ml-2 block text-sm text-gray-900">
                Acepto los{' '}
                <a href="/legal/terms/" className="text-blue-600 hover:underline font-medium">
                  términos y condiciones
                </a>
                , la{' '}
                <a href="/legal/privacy/" className="text-blue-600 hover:underline font-medium">
                  política de privacidad
                </a>
                {' '}y la{' '}
                <a href="/legal/data-policy/" className="text-blue-600 hover:underline font-medium">
                  política de datos
                </a>{' '}
                *
              </label>
            </div>
            {errors.aceptoTerminos && (
              <p className="mt-1 text-sm text-red-600">{errors.aceptoTerminos.message}</p>
            )}

            <div className="mt-1">
              <button
                type="button"
                onClick={() => setShowRefField(!showRefField)}
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                {showRefField ? '▼' : '▶'} ¿Tienes un código de referido?
              </button>

              {showRefField && (
                <div className="mt-2">
                  <label htmlFor="refCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Código de referido
                  </label>
                  <div className="relative">
                    <input
                      id="refCode"
                      type="text"
                      value={refCode}
                      onChange={(e) => {
                        const code = e.target.value.toUpperCase().replace(/\s/g, '');
                        setRefCode(code);
                        setRefCodeValid(null);
                        debouncedValidateRef(code);
                      }}
                      autoComplete="off"
                      placeholder="Ej: JUANPANADERIA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg uppercase tracking-wide"
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                    />
                    {refCodeValid === true && (
                      <span className="absolute right-3 top-2.5 text-green-500" aria-hidden>
                        ✓
                      </span>
                    )}
                    {refCodeValid === false && refCode.length > 0 && (
                      <span className="absolute right-3 top-2.5 text-red-500" aria-hidden>
                        ✕
                      </span>
                    )}
                  </div>

                  {refCodeValid === true && refCodeData && (
                    <p className="mt-1 text-sm text-green-600">
                      Referido por: {refCodeData.referrer_nombre}
                    </p>
                  )}

                  {refCodeValid === false && refCode.length > 0 && (
                    <p className="mt-1 text-sm text-red-500">Código no encontrado</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
            </button>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              ¿Ya tienes una cuenta?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Inicia sesión aquí
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};